import { useSearchParams } from 'react-router';
import { uuid } from '../search/state';
import type { JobView } from '@api/devices/contracts';
import { ChevronLeft, ChevronRight, ClipboardList, KeyRound, Search } from 'lucide-react';
import { useEffect, useState } from 'react';
import { BatchApprovalDialog } from '../operations/batch';
import { ApprovalDialog, CHECKABLE, closedByRemoval, JobRow, RegisterKeyButton, useCapabilities } from '../operations/jobs';
import { useApprovalKeys, useFinishedJobs, useJobs, useJob } from '../search/hooks';
import { useDebounced } from '../shell/CommandPalette';
import { useSession } from '../session/session';
import { Button } from '../ui/Button';
import { ErrorPanel } from '../ui/errors';
import { Card, EmptyState, SectionTitle, Skeleton } from '../ui/misc';
import { PageHeader } from './PageHeader';
import { matchesWorkQuery, WORK_PAGE_SIZE } from './work-list';

const GROUPS: Array<{ key: string; title: string; note: string; match: (job: JobView) => boolean }> = [
  { key: 'approve', title: 'Waiting for approval', note: 'Nothing runs until an approver confirms with a security key.', match: j => j.state === 'awaiting_approval' },
  { key: 'moving', title: 'In progress', note: 'Queued for, collected by, or running on a device.', match: j => ['queued', 'delivered', 'running'].includes(j.state) },
  { key: 'check', title: 'Needs a check', note: 'The device never reported a result, or the job failed. Mark it as checked once you’ve looked.',
    match: j => CHECKABLE.includes(j.state) && !j.checked && !closedByRemoval(j) },
];
const FINISHED = { key: 'done', title: 'Finished', note: 'Succeeded, canceled, rejected, expired, checked after it failed, or closed by removing its device.' };

function Pager({ title, page, pages, start, count, total, onPage }: { title: string; page: number; pages: number; start: number; count: number; total: number; onPage: (page: number) => void }) {
  if (pages <= 1) return null;
  return (
    <div className="flex items-center gap-3 border-t border-line px-4 py-2.5 text-[13px] text-ink-3">
      <span className="tabular">{start + 1}–{start + count} of {total}</span>
      <span className="flex-1" />
      <span className="flex gap-1">
        <Button size="icon" variant="ghost" aria-label={`Previous page of ${title}`} disabled={page <= 1} onClick={() => onPage(page - 1)}><ChevronLeft className="size-4" /></Button>
        <Button size="icon" variant="ghost" aria-label={`Next page of ${title}`} disabled={page >= pages} onClick={() => onPage(page + 1)}><ChevronRight className="size-4" /></Button>
      </span>
    </div>
  );
}

/** Open work from the bounded list the sidebar polls, and finished history one page at a time. */
export function WorkPage() {
  const jobs = useJobs();
  const [params] = useSearchParams();
  const selectedId = params.get('job');
  const selected = useJob(selectedId && uuid.test(selectedId) ? selectedId : null);
  const caps = useCapabilities();
  const keys = useApprovalKeys();
  const { session } = useSession();
  const [approving, setApproving] = useState<JobView | null>(null);
  const [approvingBatch, setApprovingBatch] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [pages, setPages] = useState<Record<string, number>>({});
  // Open groups filter as the person types. Finished waits until typing pauses, like the other server searches.
  const historyQuery = useDebounced(query, 220);
  const [historyFor, setHistoryFor] = useState(historyQuery);
  const storedFinished = pages.done ?? 1;
  const finishedPage = historyFor === historyQuery ? storedFinished : 1;
  if (historyFor !== historyQuery) {
    setHistoryFor(historyQuery);
    if (storedFinished !== 1) setPages(current => ({ ...current, done: 1 }));
  }
  const finished = useFinishedJobs(historyQuery, finishedPage);
  const review = (job: JobView) => (job.batchId ? setApprovingBatch(job.batchId) : setApproving(job));
  const all = jobs.data ?? [];
  const setPage = (key: string, page: number) => setPages(current => ({ ...current, [key]: page }));
  const open = GROUPS.map(g => {
    const ordered = all.filter(g.match).filter(job => matchesWorkQuery(job, query));
    const pageCount = Math.max(1, Math.ceil(ordered.length / WORK_PAGE_SIZE));
    const page = Math.min(pages[g.key] ?? 1, pageCount);
    const start = (page - 1) * WORK_PAGE_SIZE;
    return { ...g, ordered, page, pageCount, start, visible: ordered.slice(start, start + WORK_PAGE_SIZE) };
  });
  const finishedTotal = finished.data?.total ?? 0;
  const finishedJobs = finished.data?.jobs ?? [];
  const finishedPages = Math.max(1, Math.ceil(finishedTotal / WORK_PAGE_SIZE));
  // A page that the latest total no longer contains (an Undo emptied it) must be requested again, not polled forever.
  useEffect(() => {
    if (finished.data && finishedPage > finishedPages) setPage(FINISHED.key, finishedPages);
  }, [finished.data, finishedPage, finishedPages]);
  const shownPage = Math.min(finishedPage, finishedPages);
  const finishedStart = (shownPage - 1) * WORK_PAGE_SIZE;
  const loading = jobs.isPending || (finished.isPending && finished.data === undefined);
  const nothingOpen = open.every(g => g.ordered.length === 0);
  const historyMissing = finished.isError && finishedTotal === 0;
  const searchBox = (
    <label className="relative flex items-center">
      <Search className="pointer-events-none absolute left-3.5 size-4 text-ink-3" />
      <input type="search" value={query} maxLength={200} onChange={e => {
        const next = e.target.value.slice(0, 200);
        setQuery(next);
        setPages(current => {
          const kept: Record<string, number> = {};
          if (current.done !== undefined) kept.done = current.done;
          return kept;
        });
      }} spellCheck={false}
        placeholder="Filter by device, customer, runbook, reason or requester" aria-label="Filter work"
        className="h-11 w-full rounded-xl border border-line bg-panel pl-10 pr-4 text-[15px] shadow-card outline-none transition-colors placeholder:text-ink-3 focus:border-brand/50 focus:ring-4 focus:ring-brand/10" />
    </label>
  );
  return (
    <div className="space-y-6">
      <PageHeader title="Work" description="Runbooks and scripts across your devices, with who asked, who approved and what each device reported." />
      {selectedId && <section aria-label="Selected job"><Card className="overflow-hidden">
        {selected.isError ? <ErrorPanel error={selected.error} onRetry={() => void selected.refetch()} />
          : selected.data ? <JobRow key={selected.data.id} job={selected.data} defaultOpen onApprove={review} />
            : selectedId && !uuid.test(selectedId) ? <p className="p-4">Invalid job link.</p> : <Skeleton className="m-4 h-16" />}
      </Card></section>}
      {caps.approve && session.approvalKeys === 0 && keys.data?.length === 0 && (
        <Card className="flex flex-wrap items-center gap-3 p-4">
          <KeyRound className="size-5 text-attention" />
          <p className="min-w-0 flex-1 text-[13.5px]"><span className="font-semibold">Register a security key to approve jobs.</span> <span className="text-ink-2">Approvals are signed by a hardware key or passkey, separate from your Google sign-in.</span></p>
          <RegisterKeyButton variant="primary" />
        </Card>
      )}
      {jobs.isError ? <Card><ErrorPanel error={jobs.error} onRetry={() => void jobs.refetch()} /></Card>
        : loading ? <Card className="space-y-3 p-4">{[0, 1, 2].map(i => <Skeleton key={i} className="h-10 w-full" />)}</Card>
        : historyMissing && nothingOpen ? <>
          {(query || all.length > 0) && searchBox}
          <Card><ErrorPanel error={finished.error} onRetry={() => void finished.refetch()} /></Card>
        </>
        : !all.length && finishedTotal === 0 && !query ? <Card><EmptyState icon={<ClipboardList className="size-5" />} title="No jobs yet">Run a runbook or a script from a device page; it will be tracked here from request to result.</EmptyState></Card>
        : <>
          {searchBox}
          {nothingOpen && finishedTotal === 0
            ? <Card><EmptyState icon={<Search className="size-5" />} title="Nothing matches">Try a device, customer, runbook, reason or the person who asked.</EmptyState></Card>
            : <>
              {open.map(g => g.ordered.length === 0 ? null : (
                <section key={g.key} className="space-y-2">
                  <SectionTitle aside={<span className="text-[12.5px] text-ink-3">{g.note}</span>}>{g.title} · {g.ordered.length}</SectionTitle>
                  <Card className="overflow-hidden">
                    {g.visible.map(j => <JobRow key={j.id} job={j} onApprove={review} />)}
                    <Pager title={g.title} page={g.page} pages={g.pageCount} start={g.start} count={g.visible.length} total={g.ordered.length} onPage={page => setPage(g.key, page)} />
                  </Card>
                </section>
              ))}
              {finished.isError ? <Card><ErrorPanel error={finished.error} onRetry={() => void finished.refetch()} /></Card>
                : finishedTotal > 0 && (
                  <section className="space-y-2">
                    <SectionTitle aside={<span className="text-[12.5px] text-ink-3">{FINISHED.note}</span>}>{FINISHED.title} · {finishedTotal}</SectionTitle>
                    <Card className="overflow-hidden">
                      {finishedJobs.map(j => <JobRow key={j.id} job={j} onApprove={review} />)}
                      <Pager title={FINISHED.title} page={shownPage} pages={finishedPages} start={finishedStart} count={finishedJobs.length} total={finishedTotal} onPage={page => setPage(FINISHED.key, page)} />
                    </Card>
                  </section>
                )}
            </>}
        </>}
      <ApprovalDialog job={approving} open={!!approving} onOpenChange={o => { if (!o) setApproving(null); }} />
      <BatchApprovalDialog batchId={approvingBatch} open={!!approvingBatch} onOpenChange={o => { if (!o) setApprovingBatch(null); }} onApproveOne={setApproving} />
    </div>
  );
}
